import { useState, useEffect } from "react";
import api from "@/lib/api";
import { X, Plus, Trash2, Shield, Users, Globe, Copy } from "lucide-react";
import { toast } from "sonner";

export default function PublishDialog({ dashboard, columns, onClose, onSaved }) {
  const [publish, setPublish] = useState(dashboard.published);
  const [isPublic, setIsPublic] = useState(dashboard.is_public ?? false);
  const [enabled, setEnabled] = useState(dashboard.access_control?.enabled ?? false);
  const [column, setColumn] = useState(dashboard.access_control?.column || columns?.[0] || "");
  const [rules, setRules] = useState(Object.entries(dashboard.access_control?.rules || {}).map(([email, value]) => ({ email, value })));
  const [team, setTeam] = useState([]);
  const [showTeam, setShowTeam] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/team").then(({ data }) => setTeam(data.members || [])).catch(() => {}); }, []);

  const addRule = () => setRules([...rules, { email: "", value: "" }]);
  const upd = (i, k, v) => setRules(rules.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const rm = (i) => setRules(rules.filter((_, idx) => idx !== i));
  const addFromTeam = (m) => {
    if (rules.some((r) => r.email.toLowerCase() === m.email.toLowerCase())) return;
    const suggested = column && m.attributes?.[column] ? m.attributes[column] : "";
    setRules([...rules, { email: m.email, value: suggested }]);
  };
  const bulkAdd = () => {
    const has = new Set(rules.map(r => r.email.toLowerCase()));
    const adds = team.filter(m => !has.has(m.email.toLowerCase())).map(m => ({ email: m.email, value: column && m.attributes?.[column] ? m.attributes[column] : "" }));
    setRules([...rules, ...adds]); setShowTeam(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const rulesObj = {};
      rules.forEach(r => { if (r.email.trim() && r.value.trim()) rulesObj[r.email.trim().toLowerCase()] = r.value.trim(); });
      const { data } = await api.post(`/dashboards/${dashboard.id}/publish`, {
        published: publish,
        is_public: isPublic,
        access_control: { enabled, column: enabled ? column : null, rules: rulesObj },
      });
      toast.success(publish ? "Published" : "Unpublished");
      onSaved?.(); onClose();
      if (isPublic && data.public_token) {
        const url = `${window.location.origin}/public/d/${data.public_token}`;
        navigator.clipboard.writeText(url);
        toast.success("Public link copied to clipboard");
      }
    } catch (err) {
      const d = err?.response?.data?.detail || "Failed";
      toast.error(typeof d === "string" ? d : "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="publish-dialog">
      <div className="bg-white border border-[var(--border)] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto scroll-thin shadow-xl">
        <div className="p-6 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <div className="label-caps mb-1">Publish & Share</div>
            <h2 className="font-display text-xl font-medium">{dashboard.title}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="close-publish"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-6">
          <Toggle label="Publish to registered users" body="Once published, invited users in your workspace can view this dashboard."
            checked={publish} onChange={setPublish} testId="publish-toggle" />

          <Toggle label={<span className="flex items-center gap-2"><Globe size={12} /> Public dashboard</span>}
            body="Anyone with the link can view this dashboard without signing in. Use for public reports."
            checked={isPublic} onChange={setIsPublic} disabled={!publish} testId="public-toggle" />

          <div className="border-t border-slate-100 pt-6">
            <Toggle label={<span className="flex items-center gap-2"><Shield size={12} /> Row-level access control</span>}
              body="Filter what each user sees based on a column value. Ignored for public dashboards."
              checked={enabled} onChange={setEnabled} disabled={!publish || isPublic} testId="ac-toggle" />
          </div>

          {enabled && !isPublic && (
            <div className="space-y-4">
              <div>
                <label className="label-caps block mb-2">Filter column</label>
                <select data-testid="ac-column" value={column} onChange={(e) => setColumn(e.target.value)} className="input-base">
                  {columns.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <div className="text-xs text-slate-500 mt-2">
                  Users only see rows where <span className="mono text-slate-800">{column} = &lt;their value&gt;</span>.
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="label-caps">Access rules</span>
                  <div className="flex gap-3">
                    <button type="button" onClick={() => setShowTeam(!showTeam)} data-testid="show-team" className="text-xs text-[var(--primary)] hover:underline flex items-center gap-1"><Users size={12} /> From directory</button>
                    <button type="button" onClick={addRule} data-testid="add-rule" className="text-xs text-[var(--primary)] hover:underline flex items-center gap-1"><Plus size={12} /> Manual</button>
                  </div>
                </div>

                {showTeam && (
                  <div className="border border-[var(--border)] rounded-lg mb-3 max-h-[220px] overflow-y-auto scroll-thin" data-testid="team-picker">
                    <div className="px-3 py-2 border-b border-[var(--border)] flex items-center justify-between bg-slate-50">
                      <span className="text-xs text-slate-500">{team.length} team members</span>
                      {team.length > 0 && <button type="button" onClick={bulkAdd} className="text-[10px] text-[var(--primary)] hover:underline">Add all</button>}
                    </div>
                    {team.length === 0 ? <div className="p-3 text-xs text-slate-500">Add members in the Team Directory first.</div>
                    : team.map(m => {
                      const has = rules.some(r => r.email.toLowerCase() === m.email.toLowerCase());
                      const sug = column && m.attributes?.[column];
                      return (
                        <button key={m.id} type="button" onClick={() => addFromTeam(m)} disabled={has} data-testid={`pick-${m.id}`}
                          className={`w-full text-left px-3 py-2 border-b border-slate-100 flex items-center justify-between text-xs ${has ? "text-slate-400" : "hover:bg-slate-50 text-slate-700"}`}>
                          <div>
                            <div className="font-medium">{m.name}</div>
                            <div className="text-slate-500 mono">{m.email}</div>
                          </div>
                          {sug && <span className="chip chip-primary">{column}={sug}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}

                {rules.length === 0 && <div className="text-xs text-slate-500">No rules yet — no user will have access.</div>}
                <div className="space-y-2">
                  {rules.map((r, i) => (
                    <div key={i} className="flex gap-2 items-center" data-testid={`rule-${i}`}>
                      <input placeholder="user@company.com" value={r.email} onChange={(e) => upd(i, "email", e.target.value)} className="input-base flex-1" />
                      <span className="text-slate-400 text-sm">→</span>
                      <input placeholder={`allowed ${column} value`} value={r.value} onChange={(e) => upd(i, "value", e.target.value)} className="input-base flex-1" />
                      <button type="button" onClick={() => rm(i)} className="text-slate-400 hover:text-[var(--danger)]"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-[var(--border)] flex justify-end gap-3 bg-slate-50/50">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="publish-save">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function Toggle({ label, body, checked, onChange, disabled, testId }) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? "opacity-50" : "cursor-pointer"}`}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} className="w-4 h-4 mt-0.5" data-testid={testId} />
      <div>
        <div className="text-sm font-medium text-slate-900">{label}</div>
        <div className="text-xs text-slate-500 mt-0.5">{body}</div>
      </div>
    </label>
  );
}
